import Component from "../component";
import Urls from "../../configuration/urls.config";
import { loadScript } from "../../utils/load_script";
import Selectors from "../../configuration/selectors.config";
import { AssetFeatureResponse } from "../../types/stores";
import Styler from "../../helpers/styler";
import { replace, debounce, getMarkerIcon } from "../../utils/utils";
import { getLocale, getLocaleLang } from "../../helpers/locale";
import { getConfig } from "../../configuration/config";
export interface IMapComponent {
    woosmapPublicKey: string;
    mapOptions: woosmap.map.MapOptions;
    storesStyle: woosmap.map.Style;
    nearbyLocation?: woosmap.map.LatLngLiteral;
    selectedStore?: AssetFeatureResponse;
    stores?: woosmap.map.GeoJSONFeature[];
    query?: string;
    padding: woosmap.map.Padding;
    userCurrentPosition?: woosmap.map.LatLngLiteral;
    zoom?: number;
    filter?: string;
}

export enum MapComponentEvents {
    STORES_CHANGED = "stores_changed",
    STORE_SELECTED = "store_selected",
    STORE_UNSELECTED = "store_unselected",
    FILTERS_UPDATED = "filters_updated",
    MAP_READY = "map_ready",
    MAP_IDLE = "map_idle",
    MAP_BOUNDS_CHANGED = "map_bounds_changed",
    MAP_PANNED = 'map_pan',
}

export default class MapComponent extends Component<IMapComponent> {
    public map!: woosmap.map.Map;
    private storesOverlay!: woosmap.map.StoresOverlay;
    private styler!: Styler;
    private bounds_listener!: woosmap.map.MapEventListener;
    private storeMarkers: woosmap.map.Marker[] = [];
    private selectedStoreMarker!: woosmap.map.Marker | undefined;

    init(): void {
        this.$element = document.createElement("div");
        this.$element.id = Selectors.mapWrapperID;
        this.$target.appendChild(this.$element);
    }

    render(): void {
        if (this.state && this.$element) {
            loadScript({
                url: Urls.mapJS,
                params: { key: this.state.woosmapPublicKey, language: getLocaleLang().toLowerCase() },
                override: true
            })
                .then(() => {
                    this.initMapView();
                })
                .catch((error) => {
                    console.error(
                        replace(getLocale().errors.loadingLibrary, { 'library': "Woosmap Map JS SDK script" }),
                        error
                    );
                });
        }
    }

    initMapView(): void {
        this.map = new woosmap.map.Map(
            Selectors.mapWrapperID,
            Object.assign({}, this.state.mapOptions, { styles: getConfig().map.mapStyleOptions })
        );
        this.styler = new Styler(this.state.storesStyle);
        this.storesOverlay = new woosmap.map.StoresOverlay(this.state.storesStyle);
        if (this.state.filter) {
            this.storesOverlay.setQuery(this.state.filter);
        } else {
            this.storesOverlay.setQuery('');
        }
        this.storesOverlay.setMap(this.map);

        woosmap.map.event.addListener(this.map, "store_selected", (store) => {
            this.handleSelectedStore(store);
        });

        woosmap.map.event.addListener(this.map, "store_unselected", (store) => {
            this.handleUnSelectedStore(store);
        });

        woosmap.map.event.addListenerOnce(this.map, "idle", () => {
            this.$element.classList.add("mapReady");
            this.emit(MapComponentEvents.MAP_IDLE);
        });
        this.bounds_listener = woosmap.map.event.addListener(this.map, "bounds_changed", debounce(() => {
            const bounds = this.map.getBounds();
            this.emit(MapComponentEvents.MAP_BOUNDS_CHANGED, bounds);
        }, 50));

        this.on(MapComponentEvents.STORES_CHANGED, () => {
            this.handleStores();
        });
        this.on(MapComponentEvents.STORE_SELECTED, () => {
            this.selectStore();
        });
        this.on(MapComponentEvents.STORE_UNSELECTED, () => {
            this.selectStore();
        });
        this.on(MapComponentEvents.FILTERS_UPDATED, () => {
            this.updateFilters();
        });
        this.emit(MapComponentEvents.MAP_READY);
        this.on(MapComponentEvents.MAP_PANNED, (direction) => {
            this.panMap(direction);
        })
    }

    handleSelectedStore(store: AssetFeatureResponse): void {
        if (!this.state.selectedStore) {
            this.setState({ selectedStore: store }, true, () => {
                this.emit(MapComponentEvents.STORE_SELECTED, this.state.selectedStore);
            });
        }
    }
    handleStores(): void {
        if (this.state.stores) {
            if (this.state.stores.length > 0) {
                this.removeBoundsListener();
                if (this.storeMarkers.length > 0) {
                    this.storeMarkers.forEach((marker) => {
                        marker.setMap(null);
                    });
                    this.storeMarkers = [];
                }
                this.state.stores.forEach(element => {
                    const feature: AssetFeatureResponse = element as AssetFeatureResponse;
                    const type = feature.properties?.types?.[0] ?? "JR";
                    const LatLngPoint = element.geometry as woosmap.map.GeoJSONPoint;
                    const marker = new woosmap.map.Marker({
                        position: { lat: LatLngPoint.coordinates[1], lng: LatLngPoint.coordinates[0] },
                        icon: getMarkerIcon(type, false),
                        title: feature.properties.name
                    });
                    marker.set("store_id", feature.properties.store_id);
                    marker.set("brand", type);
                    this.onStoreMarkerClicked(marker, type, feature)
                    this.storeMarkers.push(marker);
                    marker.setMap(this.map);
                });

                const bounds = new woosmap.map.LatLngBounds();

                this.state.stores.forEach((feature) => {
                    const LatLngPoint = feature.geometry as woosmap.map.GeoJSONPoint;
                    bounds.extend({ lat: LatLngPoint.coordinates[1], lng: LatLngPoint.coordinates[0] });
                });
                if (this.state.nearbyLocation) {
                    const nearBy = this.state.nearbyLocation;
                    bounds.extend(nearBy);
                }
                if (!this.state.nearbyLocation) {
                    if (this.state.stores.length === 1) {
                        const feature: AssetFeatureResponse = this.state.stores[0] as AssetFeatureResponse;
                        const storeLocation = feature.geometry.coordinates;
                        this.map.flyTo({
                            center: { lat: storeLocation[1], lng: storeLocation[0] },
                            zoom: this.state.zoom,
                            padding: this.state.padding,
                            animate: true
                        });
                    } else {
                        this.map.fitBounds(bounds, this.state.padding);
                    }
                }
                else {
                    // The user location & the nearest stores should be in viewport
                    this.map.fitBounds(bounds, this.state.padding, true);
                }
                this.storesOverlay.setMap(null);
            } else {
                if (this.storeMarkers.length > 0) {
                    this.storeMarkers.forEach((marker) => {
                        marker.setMap(null);
                    });
                    this.storeMarkers = [];
                }
                this.storesOverlay.setMap(this.map);

            }
        }
    }

    unselectStoreOnDataOverlay(): void {
        this.resetToDefaultIcon();
    }

    selectStoreOnDataOverlay(store?: AssetFeatureResponse): void {
        const selectedStore = store ? JSON.parse(JSON.stringify(store)) : JSON.parse(JSON.stringify(this.state.selectedStore));
        if (this.storeMarkers && this.storeMarkers.length > 0) {
            this.setStoreIcon(selectedStore);
        }
    }

    selectStore(): void {
        if (this.state.selectedStore) {
            const visibleBounds = this.map.getBounds(this.state.padding);
            const selectedStore = this.state.selectedStore;
            const latLng: woosmap.map.LatLngLiteral = {
                lng: selectedStore.geometry.coordinates[0],
                lat: selectedStore.geometry.coordinates[1],
            };
            if (latLng) {
                if (!visibleBounds.contains(latLng)) {
                    this.map.panTo(latLng, this.state.padding);
                }
            }
            this.setStoreIcon(this.state.selectedStore)
        }
    }
    setStoreIcon(store: AssetFeatureResponse): void {
        const type = store.properties?.types?.[0] ?? "JR";
        if (this.storeMarkers && this.storeMarkers.length > 0) {
            this.storeMarkers.forEach(element => {
                element.setIcon(getMarkerIcon(type, false));
            });
            const markerToStyle = this.storeMarkers.find(
                (m) => m && m.get("store_id") === store.properties.store_id,
            );
            if (markerToStyle) {
                this.selectedStoreMarker = markerToStyle;
                markerToStyle.setIcon(getMarkerIcon(type, true));
            }
        }
    }
    resetToDefaultIcon(): void {
        if (this.storeMarkers && this.storeMarkers.length > 0) {
            this.storeMarkers.forEach(element => {
                element.setIcon(getMarkerIcon(element.get("brand"), false));
            });
        }
    }

    updateFilters(): void {
        if (this.state.query) {
            this.storesOverlay.setQuery(this.state.query);
        } else {
            this.storesOverlay.setQuery(undefined);
        }
    }

    centerMapToUserPosition(): void {

        if (this.map && this.state.zoom && this.state.userCurrentPosition) {
            this.$element.style.pointerEvents = "none";
            this.map.flyTo({
                center: this.state.userCurrentPosition,
                zoom: this.state.zoom,
                padding: this.state.padding,
                animate: true,
                duration: 3000,
            });
            setTimeout(() => {
                this.$element.style.pointerEvents = "";
            }, 3000);

        }
    }
    handleUnSelectedStore(store: AssetFeatureResponse): void {
        this.storesOverlay.clearSelection();
        this.state.selectedStore = undefined;
        this.emit(MapComponentEvents.STORE_UNSELECTED, this.state.stores);
    }
    disableUserInteraction(): void {
        this.$element.style.pointerEvents = "none";
    }
    enableUserInteraction(): void {
        setTimeout(() => {
            this.$element.style.pointerEvents = "";
        }, 3000);
    }
    showSingleStoreMarker(store: AssetFeatureResponse): void {
        this.storesOverlay.setMap(null);
        const type = store.properties?.types?.[0] ?? "JR";
        const LatLngPoint = store.geometry as woosmap.map.GeoJSONPoint;
        const marker = new woosmap.map.Marker({
            position: { lat: LatLngPoint.coordinates[1], lng: LatLngPoint.coordinates[0] },
            icon: getMarkerIcon(type, false),
            title: store.properties.name
        });
        this.onStoreMarkerClicked(marker, type, store);
        marker.setMap(this.map);
        this.storeMarkers.push(marker);

    }
    hideSingleStoreMarker(): void {
        this.storesOverlay.setMap(this.map);
        this.storeMarkers.forEach((marker) => marker.setMap(null));
        this.storeMarkers = [];
    }
    removeBoundsListener(): void {
        this.bounds_listener.remove();
    }
    panMap(direction: string): void {
        if (this.map) {
            const offset = 100;
            switch (direction) {
                case 'up': this.map.panBy(0, -offset); break;
                case 'down': this.map.panBy(0, offset); break;
                case 'left': this.map.panBy(-offset, 0); break;
                case 'right': this.map.panBy(offset, 0); break;
            }
        }
    }
    onStoreMarkerClicked(marker: woosmap.map.Marker, type: string, store: AssetFeatureResponse): void {
        marker.addListener('click', () => {
            const isSameMarker = this.selectedStoreMarker === marker;
            if (isSameMarker) {
                marker.setIcon(getMarkerIcon(type, false));
                this.selectedStoreMarker = undefined;
                if (this.state.selectedStore) {
                    this.handleUnSelectedStore(this.state.selectedStore);
                }
            }
            else {
                if (this.selectedStoreMarker) {
                    this.selectedStoreMarker.setIcon(getMarkerIcon(type, false));
                    if (this.state.selectedStore) {
                        this.handleUnSelectedStore(this.state.selectedStore);
                    }

                }
                this.selectedStoreMarker = marker;
                marker.setIcon(getMarkerIcon(type, true));
                this.handleSelectedStore(store);
            }
        })
    }
}
